import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../lib/api";
import { personDisplayName as fullName } from "@family/shared";
import type { MeResponse, Person, PersonsListResponse } from "@family/shared";

interface Props {
  familySlug: string;
  me: MeResponse;
}

/**
 * "Which person in the family tree are you?"
 *
 * Setting this is what lets the app answer "how are we related?" — on a
 * relative's page and in the chat. Without it we can only guess from your
 * profile name.
 */
export function TreeSelfLink({ familySlug, me }: Props) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [picking, setPicking] = useState(false);

  const linkedId = me.user.linkedPersonId;

  const linkedQuery = useQuery<Person>({
    queryKey: ["person", familySlug, linkedId],
    queryFn: () =>
      apiFetch<Person>(`/families/${familySlug}/persons/${linkedId}`),
    enabled: Boolean(linkedId),
  });

  const results = useQuery<PersonsListResponse>({
    queryKey: ["persons", familySlug, search],
    queryFn: () =>
      apiFetch<PersonsListResponse>(
        `/families/${familySlug}/persons?q=${encodeURIComponent(search)}`,
      ),
    enabled: search.trim().length >= 2,
  });

  const link = useMutation<{ linkedPersonId: string | null }, Error, string | null>({
    mutationFn: (personId) =>
      apiFetch(`/families/${familySlug}/me/person`, {
        method: "PUT",
        body: JSON.stringify({ personId }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      setPicking(false);
      setSearch("");
    },
  });

  const candidates = useMemo(
    () => (results.data?.persons ?? []).slice(0, 6),
    [results.data],
  );

  return (
    <div className="stack" style={{ maxWidth: 560 }}>
      <div>
        <label style={{ display: "block" }}>You in the family tree</label>
        <p className="text-subtle" style={{ fontSize: "0.8rem", margin: "2px 0 8px" }}>
          Tell us which person you are, and the app can show how you're related
          to everyone else — on their page and in the family AI.
        </p>
      </div>

      {linkedId && !picking && (
        <div className="row-between card" style={{ padding: "10px 14px" }}>
          <span>
            You are <strong>{linkedQuery.data ? fullName(linkedQuery.data) : "…"}</strong>
          </span>
          <span style={{ display: "flex", gap: "var(--s-2)" }}>
            <button
              type="button"
              className="btn-tertiary"
              onClick={() => setPicking(true)}
            >
              Change
            </button>
            <button
              type="button"
              className="btn-tertiary"
              onClick={() => link.mutate(null)}
              disabled={link.isPending}
              style={{ color: "var(--c-danger)" }}
            >
              Remove
            </button>
          </span>
        </div>
      )}

      {(!linkedId || picking) && (
        <div className="stack">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search your name in the tree…"
            aria-label="Search for yourself in the family tree"
          />
          {search.trim().length >= 2 && candidates.length === 0 && !results.isLoading && (
            <p className="text-muted" style={{ margin: 0, fontSize: "0.9rem" }}>
              No match. Ask an admin to add you to the tree first.
            </p>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {candidates.map((p) => (
              <button
                key={p.id}
                type="button"
                className="btn-secondary"
                onClick={() => link.mutate(p.id)}
                disabled={link.isPending}
                style={{ justifyContent: "flex-start", textAlign: "left" }}
              >
                {fullName(p)}
                {p.birthDate ? ` (${p.birthDate.slice(0, 4)})` : ""}
              </button>
            ))}
          </div>
          {picking && (
            <button
              type="button"
              className="btn-tertiary"
              onClick={() => {
                setPicking(false);
                setSearch("");
              }}
              style={{ alignSelf: "flex-start" }}
            >
              Cancel
            </button>
          )}
        </div>
      )}

      {link.error && <p className="text-error">{link.error.message}</p>}
    </div>
  );
}
